import { atlasRoutes } from "@/app/router/routes";
import { api } from "@/modules/api";
import styled from "@emotion/styled";
import { Button, Heading, Text, Flex, Box } from "@radix-ui/themes";
import { FC, useState } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { useNavigate } from "react-router";

interface AccountItem {
  from_firestore_id: string;
  to_account_id: string;
}

interface CreateBatchAccountUpgradeFormData {
  account_items: AccountItem[];
}
export const BatchAccountUpgradeCreatePage: FC = () => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [clipboardSuccess, setClipboardSuccess] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    getValues,
  } = useForm<CreateBatchAccountUpgradeFormData>({
    defaultValues: {
      account_items: [
        {
          from_firestore_id: "",
          to_account_id: "",
        },
      ],
    },
  });

  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: "account_items",
  });

  const confirmSubmit = () => {
    setShowConfirmation(true);
  };

  const onSubmit = async (data: CreateBatchAccountUpgradeFormData) => {
    setIsSubmitting(true);
    setError(null);
    setShowConfirmation(false);

    try {
      // Transform the form data to match the expected API format
      const accountList = data.account_items.map(item => {
        return {
          kind: "MANUAL_TARGET",
          from_firestore_id: item.from_firestore_id,
          to_account_id: item.to_account_id,
          /** Always true. */
          mark_as_force_upgraded: true,
        };
      });

      const response = await api.post("/internal/v1/v1-batch-account-upgrades/create", {
        body: {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          account_list: accountList as any,
          generate_unique_ids_for_imported_items: false,
        },
      });

      const batchAccountUpgradeId = response.data?.v1_batch_account_upgrade_id;

      if (batchAccountUpgradeId) {
        navigate(
          atlasRoutes.batchAccountUpgradesView({
            params: { batchAccountUpgradeId },
          }).path
        );
      } else {
        setError("Failed to create batch account upgrade. No ID returned.");
      }
    } catch (err) {
      setError(
        `Failed to create batch account upgrade: ${err instanceof Error ? err.message : String(err)}`
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const addNewRow = () => {
    append({
      from_firestore_id: "",
      to_account_id: "",
    });
  };

  const copyToClipboard = () => {
    const data = getValues();
    const csvRows = [
      // Header row
      "from_firestore_id,to_account_id",
      // Data rows
      ...data.account_items.map(item => `${item.from_firestore_id},${item.to_account_id}`),
    ];
    const csvContent = csvRows.join("\n");

    navigator.clipboard
      .writeText(csvContent)
      .then(() => {
        setClipboardSuccess("Data copied to clipboard!");
        setTimeout(() => setClipboardSuccess(null), 3000);
      })
      .catch(() => {
        setError("Failed to copy to clipboard.");
      });
  };

  const pasteFromClipboard = async () => {
    try {
      const text = await navigator.clipboard.readText();
      const rows = text.split("\n").filter(row => row.trim() !== "");

      // Skip header row if it exists
      const startIndex = rows[0].includes("from_firestore_id") ? 1 : 0;

      const items: AccountItem[] = [];

      for (let i = startIndex; i < rows.length; i++) {
        const columns = rows[i].split(",");
        if (columns.length >= 2) {
          items.push({
            from_firestore_id: columns[0].trim(),
            to_account_id: columns[1].trim(),
          });
        }
      }

      if (items.length > 0) {
        replace(items);
        setClipboardSuccess("Data imported from clipboard!");
        setTimeout(() => setClipboardSuccess(null), 3000);
      } else {
        setError("No valid data found in clipboard.");
      }
    } catch (_err) {
      setError("Failed to paste from clipboard.");
    }
  };

  return (
    <Container>
      <Heading size="6" mb="4">
        Create Batch Account Upgrade
      </Heading>

      {error && (
        <ErrorBox>
          <Text color="red">{error}</Text>
        </ErrorBox>
      )}

      {clipboardSuccess && (
        <SuccessBox>
          <Text color="green">{clipboardSuccess}</Text>
        </SuccessBox>
      )}

      <Form onSubmit={handleSubmit(confirmSubmit)}>
        <Flex justify="end" align="center" mb="4">
          <Flex gap="2">
            <Button type="button" size="1" variant="soft" onClick={copyToClipboard}>
              Copy to Clipboard
            </Button>
            <Button type="button" size="1" variant="soft" onClick={pasteFromClipboard}>
              Paste from Clipboard
            </Button>
            <Button type="button" size="1" onClick={addNewRow}>
              Add Item
            </Button>
          </Flex>
        </Flex>

        <Box mb="4">
          {/* Header row with column titles */}
          <HeaderRow>
            <Flex gap="3">
              <div style={{ flex: 2 }}>
                <Text weight="bold">Firestore ID</Text>
              </div>
              <div style={{ flex: 2 }}>
                <Text weight="bold">Target Account ID</Text>
              </div>
              <div style={{ width: "80px" }}></div>
            </Flex>
          </HeaderRow>

          {fields.map((field, index) => (
            <AccountItemRow key={field.id}>
              <Flex gap="3">
                <FormField style={{ flex: 2 }}>
                  <input
                    id={`account_items.${index}.from_firestore_id`}
                    className="text-field-input"
                    placeholder="Enter Firestore ID"
                    {...register(`account_items.${index}.from_firestore_id`, {
                      required: "Firestore ID is required",
                    })}
                  />
                  {errors.account_items?.[index]?.from_firestore_id && (
                    <Text color="red" size="1">
                      {errors.account_items[index]?.from_firestore_id?.message}
                    </Text>
                  )}
                </FormField>

                <FormField style={{ flex: 2 }}>
                  <input
                    id={`account_items.${index}.to_account_id`}
                    className="text-field-input"
                    placeholder="Enter target account ID"
                    {...register(`account_items.${index}.to_account_id`, {
                      required: "Target account ID is required",
                    })}
                  />
                  {errors.account_items?.[index]?.to_account_id && (
                    <Text color="red" size="1">
                      {errors.account_items[index]?.to_account_id?.message}
                    </Text>
                  )}
                </FormField>

                <FormField
                  style={{
                    width: "80px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {fields.length > 1 && (
                    <Button
                      type="button"
                      color="red"
                      variant="soft"
                      size="1"
                      onClick={() => remove(index)}
                    >
                      Remove
                    </Button>
                  )}
                </FormField>
              </Flex>
            </AccountItemRow>
          ))}
        </Box>

        <Flex gap="3" mt="4">
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? "Creating..." : "Create Batch Account Upgrade"}
          </Button>

          <Button
            type="button"
            variant="soft"
            onClick={() => navigate(atlasRoutes.batchAccountUpgrades({}).path)}
          >
            Cancel
          </Button>
        </Flex>
      </Form>

      {showConfirmation && (
        <ConfirmationOverlay onClick={() => setShowConfirmation(false)}>
          <ConfirmationDialog onClick={e => e.stopPropagation()}>
            <Heading size="3" mb="3">
              Confirm Batch Account Upgrade
            </Heading>
            <Text mb="2">
              Are you sure you want to create this batch account upgrade with {fields.length}{" "}
              account(s)?
            </Text>
            <Flex gap="3" justify="end">
              <Button variant="soft" onClick={() => setShowConfirmation(false)}>
                Cancel
              </Button>
              <Button onClick={() => handleSubmit(onSubmit)()}>Confirm</Button>
            </Flex>
          </ConfirmationDialog>
        </ConfirmationOverlay>
      )}
    </Container>
  );
};

const Container = styled.div`
  padding: 20px;
  min-width: 1000px;
  max-width: 1100px;
  margin: 0 auto;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
`;

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const HeaderRow = styled.div`
  padding: 12px 16px;
  margin-bottom: 8px;
  border-radius: 4px;
  background-color: rgba(0, 0, 0, 0.1);
  font-weight: bold;
`;

const AccountItemRow = styled.div`
  padding: 12px 16px;
  margin-bottom: 10px;
  border-radius: 4px;
  background-color: rgba(0, 0, 0, 0.05);
`;

const ErrorBox = styled(Box)`
  padding: 12px;
  margin-bottom: 16px;
  background-color: rgba(255, 0, 0, 0.1);
  border-radius: 4px;
`;

const SuccessBox = styled(Box)`
  padding: 12px;
  margin-bottom: 16px;
  background-color: rgba(0, 255, 0, 0.1);
  border-radius: 4px;
`;

const ConfirmationOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ConfirmationDialog = styled.div`
  background-color: white;
  padding: 24px;
  border-radius: 8px;
  width: 400px;
  max-width: 90%;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
`;
